import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { videosAPI } from '../services/api';
import VideoCard from '../components/VideoCard';

const Home = () => {
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedVideos();
  }, []);

  const fetchFeaturedVideos = async () => {
    try {
      const response = await videosAPI.getAll({ per_page: 6 });
      setFeaturedVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero Section - pulls up behind the fixed navbar for cinematic effect */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden -mt-16">
        {/* Animated background */}
        <div className="absolute inset-0 bg-battle-dark">
          <div className="absolute inset-0 bg-gradient-to-br from-army/20 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(74,93,58,0.10)_0%,transparent_70%)]" />
          {/* Grid overlay for depth */}
          <div className="absolute inset-0 opacity-5"
               style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                       backgroundSize: '50px 50px'}} />
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4">
          <div className="inline-block mb-4 px-3 py-1 border border-army rounded-full text-army-light text-xs font-semibold tracking-widest uppercase">
            The Premier Battle Rap Marketplace
          </div>
          <h1 className="text-6xl md:text-8xl font-black uppercase tracking-tighter mb-6" data-testid="hero-title">
            <span className="text-white">VERBAL</span>
            <br />
            <span className="text-brand-yellow" style={{textShadow: '0 0 60px rgba(245,230,66,0.45)'}}>WARZONE</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl max-w-xl mx-auto mb-10 font-light">
            Watch the greatest battles. Own the moments.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/browse" className="btn-primary px-8 py-4 text-lg" data-testid="browse-battles-btn">
              Browse Battles
            </Link>
            <Link to="/browse?status=exclusive" className="px-8 py-4 text-lg border border-army hover:border-brand-yellow/60 text-gray-300 hover:text-white rounded-lg font-semibold transition-all duration-300" data-testid="premium-content-btn">
              Premium Content
            </Link>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-battle-dark to-transparent" />
      </section>

      {/* Featured Battles */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="section-title text-white">
              Featured <span className="text-brand-yellow">Battles</span>
            </h2>
            <Link to="/browse" className="text-brand-yellow hover:text-brand-yellow-light font-semibold transition-colors group">
              View All <span className="inline-block group-hover:translate-x-1 transition-transform">&rarr;</span>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card animate-pulse overflow-hidden">
                  <div className="aspect-video bg-gray-800"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-800 rounded"></div>
                    <div className="h-3 bg-gray-800 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredVideos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-videos">
              {featuredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No battles available yet. Check back soon!
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section className="relative py-20 px-4 border-t border-army/20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(74,93,58,0.07)_0%,transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="section-title text-white mb-6">
            About <span className="text-brand-yellow">Verbal Warzone</span>
          </h2>
          <p className="text-gray-300 text-lg mb-4 leading-relaxed">
            Verbal Warzone is the premier destination for battle rap enthusiasts. We host and archive the most intense verbal showdowns from the best battlers in the game.
          </p>
          <p className="text-gray-500 leading-relaxed">
            Browse our extensive catalog of battles, discover your favorite artists, and access exclusive premium content.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
